import {
  Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy, signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { delay, filter, map, tap } from 'rxjs/operators';

import { ColorModeService } from '@coreui/angular';
import { IconSetService } from '@coreui/icons-angular';
import { DialogComponent } from './views/dialog/dialog.component';
import { ErrorHandlerService } from './services/error-handler';
import { DialogService } from './services/dialog.service';
import { IconSetupService } from './services/icon-setup.service';
import {LoaderComponent } from './views/loader/loader.component'
import { EnvBadgeComponent } from './views/env-badge/env-badge.component';

@Component({
    selector: 'app-root',
 template: `
 <app-loader></app-loader>
    <router-outlet></router-outlet>
    <app-dialog></app-dialog>
    @if (!onAuthRoute()) {
      <app-env-badge />
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterOutlet , DialogComponent, LoaderComponent, EnvBadgeComponent]
})
export class AppComponent implements OnInit {
  title = 'Muufree CMS';

  /** The sign-in page paints its own environment chip, so the caution stripe
   *  stands down there rather than saying the same thing twice. Only the two
   *  auth routes: /login and the branded /o/<slug> door, never the org
   *  console pages that live under it. */
  readonly onAuthRoute = signal(false);

  readonly #destroyRef: DestroyRef = inject(DestroyRef);
  readonly #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #titleService = inject(Title);

  readonly #colorModeService = inject(ColorModeService);
  readonly #iconSetService = inject(IconSetService);
  readonly #iconSetupService = inject(IconSetupService);

  constructor(
   private errorHandlerService: ErrorHandlerService,
    private dialogService: DialogService
  ) {
    this.#titleService.setTitle(this.title);
    // iconSet singleton
    this.#colorModeService.localStorageItemName.set('muufree-cms');
    this.#colorModeService.eventName.set('ColorSchemeChange');
  }

  ngOnInit(): void {
  this.errorHandlerService.error$.subscribe(message => {
      this.dialogService.showDialog({
        title: 'Error',
        message,
        okText: 'OK'
      });
    });
    
    this.#router.events.pipe(
        takeUntilDestroyed(this.#destroyRef)
      ).subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      // /login and the branded /o/<slug> door only. The org console pages
      // that live under that slug keep the stripe.
      const url = evt.urlAfterRedirects;
      this.onAuthRoute.set(/^\/(login|o\/[^/?#]+)([?#]|$)/.test(url));
    });

    this.#activatedRoute.queryParams
      .pipe(
        delay(1),
        map(params => <string>params['theme']?.match(/^[A-Za-z0-9\s]+/)?.[0]),
        filter(theme => ['dark', 'light', 'auto'].includes(theme)),
        tap(theme => {
          this.#colorModeService.colorMode.set(theme);
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  
}
